// Pick a preset from the catalog OR enter a custom title. Date defaults to
// today but can be backfilled. Free-form description.

import { useEffect, useMemo, useState } from 'react';
import type { Milestone } from '@shared/types.ts';
import {
  MILESTONE_PRESETS,
  MILESTONE_BY_SLUG,
  CATEGORY_LABELS,
  type MilestoneCategory,
} from '@shared/milestones.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell } from './SheetShell.tsx';
import {
  ConfirmDeletePanel,
  DateWithOptionalTime,
  DescriptionField,
  noonTodayLocal,
  timeIsSentinel,
  useConfirmDelete,
} from './fields.tsx';

type Props = {
  open: boolean;
  onClose: () => void;
  existing?: Milestone | null;
  /** Slugs already logged (so we can hide them from the picker) */
  usedSlugs?: Set<string>;
  onSubmit: (vars: {
    kind: string;
    title: string;
    occurred_at: number;
    notes?: string | null;
  }) => Promise<unknown> | void;
  onDelete?: () => Promise<unknown> | void;
};

export function MilestoneSheet({
  open,
  onClose,
  existing,
  usedSlugs,
  onSubmit,
  onDelete,
}: Props) {
  const [kind, setKind] = useState<string>(existing?.kind ?? '');
  const [title, setTitle] = useState<string>(existing?.title ?? '');
  const [occurredAt, setOccurredAt] = useState<number>(
    existing?.occurred_at ?? noonTodayLocal(),
  );
  const [notes, setNotes] = useState<string>(existing?.notes ?? '');
  const [filter, setFilter] = useState<MilestoneCategory | 'all'>('all');
  const [showTime, setShowTime] = useState<boolean>(() =>
    existing ? !timeIsSentinel(existing.occurred_at) : false,
  );
  const confirm = useConfirmDelete();

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setKind(existing.kind);
      setTitle(existing.title);
      setOccurredAt(existing.occurred_at);
      setNotes(existing.notes ?? '');
      setShowTime(!timeIsSentinel(existing.occurred_at));
    } else {
      setKind('');
      setTitle('');
      setOccurredAt(noonTodayLocal());
      setNotes('');
      setShowTime(false);
    }
    confirm.reset();
    setFilter('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id]);

  const presets = useMemo(
    () =>
      MILESTONE_PRESETS.filter(
        (p) =>
          (filter === 'all' || p.category === filter) &&
          (existing?.kind === p.slug || !usedSlugs?.has(p.slug)),
      ),
    [filter, usedSlugs, existing?.kind],
  );

  const pickedPreset = kind !== 'custom' && kind ? MILESTONE_BY_SLUG.get(kind) : null;
  const valid = kind === 'custom' ? title.trim().length > 0 : !!kind;

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={existing ? 'Edit milestone' : 'Log a milestone'}
      tagline={
        existing ? '' : 'Tap a preset, or pick "Custom" for anything not listed'
      }
      accent="accent"
      icon={(p) => <Icon.sparkle {...p} />}
      primaryLabel={confirm.confirming ? 'Yes, delete' : 'Save'}
      primaryDanger={confirm.confirming}
      primaryDisabled={!confirm.confirming && !valid}
      secondaryLabel={confirm.confirming ? 'Keep' : 'Cancel'}
      onPrimary={async () => {
        if (confirm.confirming && onDelete) {
          await onDelete();
          confirm.reset();
          onClose();
          return;
        }
        if (!valid) return;
        const finalTitle = kind === 'custom' ? title.trim() : pickedPreset?.title ?? title.trim();
        await onSubmit({
          kind,
          title: finalTitle,
          occurred_at: occurredAt,
          notes: notes.trim() || null,
        });
        onClose();
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!existing && (
          <>
            <div
              className="lt-scroll"
              style={{
                display: 'flex',
                gap: 6,
                overflowX: 'auto',
                margin: '0 -2px 2px',
                padding: '0 2px',
                scrollbarWidth: 'none',
              }}
            >
              {(['all', ...Object.keys(CATEGORY_LABELS)] as const).map((c) => {
                const on = c === filter;
                const label =
                  c === 'all' ? 'All' : CATEGORY_LABELS[c as MilestoneCategory];
                return (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setFilter(c as MilestoneCategory | 'all')}
                    style={{
                      flexShrink: 0,
                      padding: '6px 12px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 500,
                      background: on ? 'var(--accent-soft)' : 'var(--bg-sunken)',
                      color: on ? 'var(--accent)' : 'var(--text-2)',
                      border: on ? '1px solid var(--accent-line)' : '1px solid transparent',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 8,
                maxHeight: 280,
                overflowY: 'auto',
              }}
              className="lt-scroll"
            >
              {presets.map((p) => {
                const on = kind === p.slug;
                return (
                  <button
                    type="button"
                    key={p.slug}
                    onClick={() => {
                      setKind(p.slug);
                      setTitle(p.title);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '12px 12px',
                      borderRadius: 14,
                      background: on ? 'var(--accent-soft)' : 'var(--bg-card)',
                      border: on
                        ? '1px solid var(--accent)'
                        : '1px solid var(--border)',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: 22, lineHeight: 1 }}>{p.emoji}</span>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{p.title}</span>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => {
                  setKind('custom');
                  setTitle('');
                }}
                style={{
                  gridColumn: '1 / -1',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '12px 12px',
                  borderRadius: 14,
                  background: kind === 'custom' ? 'var(--accent-soft)' : 'var(--bg-card)',
                  border:
                    kind === 'custom'
                      ? '1px solid var(--accent)'
                      : '1px dashed var(--border-strong)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: 'var(--text-2)',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <Icon.plus s={16} /> Custom milestone
              </button>
            </div>
          </>
        )}

        {(kind === 'custom' || existing) && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 14,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.04,
                marginBottom: 4,
              }}
            >
              Title
            </div>
            <input
              type="text"
              value={title}
              autoFocus={kind === 'custom' && !existing}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. First plane ride"
              style={{ width: '100%', fontSize: 17, color: 'var(--text-1)' }}
            />
          </div>
        )}

        <DateWithOptionalTime
          occurredAt={occurredAt}
          onChange={setOccurredAt}
          showTime={showTime}
          onToggleTime={setShowTime}
        />

        <DescriptionField
          value={notes}
          onChange={setNotes}
          placeholder="The moment, who was there, anything memorable"
        />

        {onDelete && !confirm.confirming && (
          <button
            type="button"
            className="lt-btn lt-btn-ghost"
            style={{ width: '100%', padding: '10px 0', fontSize: 13, color: 'var(--danger)' }}
            onClick={confirm.startConfirm}
          >
            <Icon.trash s={14} /> Delete this milestone
          </button>
        )}
        {confirm.confirming && (
          <ConfirmDeletePanel message="This is permanent. The milestone will disappear from the timeline." />
        )}
      </div>
    </SheetShell>
  );
}
